import { Beaker, Droplets, FlaskConical, Microscope, Snowflake, ShieldCheck, Truck, Leaf } from "lucide-react";
import PurityBar from "@/components/PurityBar";

const PROCESS_STEPS = [
  { icon: Leaf, title: "Cultivation", body: "Open-pond raceways at our South Indian cultivation facility with monitored pH, salinity, and biomass density." },
  { icon: Droplets, title: "Harvest & Wash", body: "Cross-flow filtration removes excess media; biomass washed to <0.1% NaCl." },
  { icon: FlaskConical, title: "Cell Disruption", body: "Controlled hydro-mechanical lysis to release intracellular phycocyanin without denaturing." },
  { icon: Beaker, title: "Extraction", body: "Phosphate-buffer extraction at 4 °C — the gentlest viable extraction protocol." },
  { icon: Microscope, title: "Purification", body: "Ultrafiltration + chromatography to lift A620/A280 from 0.7 → 3.0+." },
  { icon: Snowflake, title: "Freeze-Drying", body: "Lyophilisation under -60 °C / 0.01 mbar to preserve colour intensity and bioactivity." },
  { icon: ShieldCheck, title: "QC & Release", body: "NABL lab tests: HPLC, heavy metals, microbial panel, endotoxin (E30 only)." },
  { icon: Truck, title: "Export", body: "Cold-chain logistics, USFDA-prior-notice ready, EU CHED documentation included." },
];

const GRADE_TABLE = [
  { grade: "E18", purity: "Food-grade evaluation", market: "Food & Beverage", review: "Launch-stage technical review", cert: "FSSAI · HACCP" },
  { grade: "E25", purity: "Cosmetic / nutraceutical evaluation", market: "Cosmetic / Nutraceutical", review: "Technical review pathway", cert: "ISO 22000 · Organic" },
  { grade: "E30", purity: "High-spec evaluation", market: "Pharma / Research", review: "Traceability-first review", cert: "WHO-GMP (in progress) · GLP" },
  { grade: "E40", purity: "Advanced technical evaluation", market: "Diagnostics · Fluorescence · Institutional", review: "Technical dossier pathway", cert: "Application-fit assessment" },
];

const Science = () => {
  return (
    <div data-testid="page-science" className="pt-[72px]">
      {/* HEADER */}
      <section className="container-wide py-20 md:py-28">
        <p className="eyebrow mb-5">◦  Science & Quality</p>
        <h1 className="font-display text-5xl md:text-6xl text-brand-black tracking-tight leading-[1.05] max-w-4xl">
          When your formula demands the <span className="italic text-brand-teal">purest blue,</span> demand a purity ratio.
        </h1>
        <p className="mt-6 text-brand-black/70 max-w-2xl leading-relaxed">
          C-Phycocyanin is the high-value blue protein-pigment complex inside Arthrospira platensis. Its value is determined by a single ratio — A620 (peak phycocyanin absorbance) divided by A280 (total protein absorbance). The higher the ratio, the higher the application tier.
        </p>
      </section>

      {/* PURITY SIGNATURE */}
      <section className="bg-brand-cream border-y border-brand-green/15">
        <div className="container-wide py-16">
          <p className="eyebrow mb-4">◦  The Purity Ratio Signature</p>
          <PurityBar value={4.0} label="A620 / A280 — global grading scale" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            {[
              { g: "E18", r: "≥ 0.7", u: "Food color" },
              { g: "E25", r: "≥ 1.5", u: "Cosmetics, nutraceuticals" },
              { g: "E30", r: "≥ 3.0", u: "Pharma, clinical" },
              { g: "Analytical", r: "≥ 4.0", u: "R&D, reference standard" },
            ].map((x) => (
              <div key={x.g} className="bg-white border border-brand-green/15 p-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50">Grade</p>
                <p className="font-display text-3xl text-brand-teal mt-1">{x.g}</p>
                <p className="font-mono text-sm text-brand-black mt-3">{x.r}</p>
                <p className="text-xs text-brand-black/65 mt-1">{x.u}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="container-wide py-24">
        <p className="eyebrow mb-4">◦  Our Extraction Process</p>
        <h2 className="font-display text-4xl md:text-5xl text-brand-black leading-tight">Eight steps. <span className="italic text-brand-teal">Zero shortcuts.</span></h2>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-green/15">
          {PROCESS_STEPS.map((s, i) => (
            <div key={s.title} className="bg-brand-cream p-7 group" data-testid={`process-step-${i}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40">STEP · {String(i + 1).padStart(2, "0")}</span>
                <s.icon size={18} className="text-brand-teal" />
              </div>
              <h3 className="font-display text-xl text-brand-black mt-5">{s.title}</h3>
              <p className="text-sm text-brand-black/65 mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* GRADE TABLE */}
      <section className="bg-brand-cream border-y border-brand-green/15">
        <div className="container-wide py-24">
          <p className="eyebrow mb-4">◦  Grade Comparison</p>
          <h2 className="font-display text-4xl md:text-5xl text-brand-black leading-tight">Specs that <span className="italic text-brand-teal">match procurement requirements.</span></h2>

          <div className="overflow-x-auto mt-10 bg-white border border-brand-green/15">
            <table className="clinical-table" data-testid="grade-comparison-table">
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Purity Positioning</th>
                  <th>Target Application</th>
                  <th>Review Pathway</th>
                  <th>Certifications</th>
                </tr>
              </thead>
              <tbody>
                {GRADE_TABLE.map((r) => (
                  <tr key={r.grade}>
                    <td className="font-display text-xl text-brand-teal">{r.grade}</td>
                    <td className="font-mono text-xs">{r.purity}</td>
                    <td>{r.market}</td>
                    <td className="font-mono text-xs">{r.review}</td>
                    <td className="text-brand-black/70">{r.cert}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* QC + SUSTAINABILITY */}
      <section className="container-wide py-24 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="bg-white border border-brand-green/15 p-10">
          <p className="eyebrow mb-3">◦  Quality Assurance</p>
          <h3 className="font-display text-3xl text-brand-black">NABL-accredited, every batch.</h3>
          <ul className="mt-6 space-y-3 text-sm text-brand-black/75">
            <li>• HPLC purity validation (UV-Vis at 620nm / 280nm)</li>
            <li>• Heavy metals panel — Pb, As, Cd, Hg (ICP-MS, &lt;0.5 ppm)</li>
            <li>• Microbiology — TPC, yeast & mold, Salmonella, E. coli, S. aureus</li>
            <li>• Pesticide residue screen (USDA NPOP &amp; EU 396/2005)</li>
            <li>• Endotoxin (LAL) for E30 pharmaceutical line</li>
          </ul>
        </div>
        <div className="bg-brand-teal text-brand-cream p-10 relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-30 mix-blend-overlay" />
          <div className="relative">
            <p className="eyebrow-light mb-3">◦  Sustainability</p>
            <h3 className="font-display text-3xl">Solar-powered. Water-frugal. Zero waste.</h3>
            <ul className="mt-6 space-y-3 text-sm text-brand-cream/85">
              <li>• 100% solar-powered cultivation infrastructure</li>
              <li>• 30× less water than soy-protein equivalent yield</li>
              <li>• Spent biomass repurposed into organic farm fertiliser</li>
              <li>• Closed-loop nutrient recovery; zero discharge to local water</li>
              <li>• Carbon footprint per kg E25: <span className="font-mono text-brand-gold">&lt; 18 kg CO₂e</span></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Science;
